import { useState } from 'react';
import { MANUAL_LIBRARY } from '../config/manualLibrary.js';

// One-tap door to the external (login-gated) manual library.
//
// Copies the model number to the clipboard first so the tech can paste it
// straight into the site's search, then opens the library in a new tab. The
// tech signs in once per device (the browser remembers it); a downloaded PDF
// comes back into the app through the normal Upload button. We never store the
// site's credentials — see src/config/manualLibrary.js.
//
// variant "prominent" — full call-to-action card, shown when a model has no
// docs yet. variant "secondary" — a quiet ghost link for when docs already
// exist but the tech wants a different manual.
export default function ManualLibraryLink({ modelNumber, variant = 'prominent' }) {
  const [copied, setCopied] = useState(false);

  async function open() {
    if (modelNumber) {
      try {
        await navigator.clipboard.writeText(modelNumber);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      } catch {
        // Clipboard access can be blocked (private mode / permissions). Not
        // fatal — the tech can still type the model into the site's search.
      }
    }
    window.open(MANUAL_LIBRARY.url, '_blank', 'noopener,noreferrer');
  }

  if (variant === 'secondary') {
    return (
      <button type="button" onClick={open} className="btn-ghost w-full text-base">
        🔎 Manual Search — find another sheet
      </button>
    );
  }

  return (
    <div className="card border-accent/40 bg-accent/5">
      <div className="flex items-start gap-3">
        <span className="text-2xl shrink-0" aria-hidden="true">🔎</span>
        <div className="min-w-0 flex-1">
          <h4 className="text-white font-bold">Need the manual?</h4>
          <p className="text-ink-300 text-sm mt-0.5">
            Log in, find the sheet{modelNumber ? ` for ${modelNumber}` : ''}, download the
            PDF, then come back and tap Upload above. Do it once — every future job on this
            model reuses it.
          </p>
        </div>
      </div>
      <button type="button" onClick={open} className="btn-primary w-full mt-3">
        🔎 Open Manual Search
      </button>
      <p className="text-ink-400 text-xs mt-2 text-center">
        {copied
          ? `✓ ${modelNumber} copied — paste it into the search box`
          : `Opens ${MANUAL_LIBRARY.name} in a new tab · you stay signed in on this device`}
      </p>
    </div>
  );
}
